import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getNotificationPreferences,
  getProjectsSubscriptionStatus,
  sendTestPushNotification,
  updateNotificationPreferences,
  type NotificationPreference,
} from '../../lib/api';
import { queryKeys } from './queryKeys';

export const useProjectsSubscriptionStatus = (token: string | null, endpoint: string | null) => {
  return useQuery({
    queryKey: queryKeys.push.subscriptions(endpoint ?? ''),
    queryFn: () => getProjectsSubscriptionStatus(token as string, endpoint as string),
    enabled: Boolean(token) && Boolean(endpoint),
  });
};

export const useNotificationPreferences = (token: string | null, projectId: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.push.preferences(projectId),
    queryFn: () => getNotificationPreferences(token as string, projectId),
    enabled: enabled && Boolean(token) && projectId.length > 0,
  });
};

export const useUpdateNotificationPreferencesMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    NotificationPreference[],
    Error,
    NotificationPreference[],
    { previous: NotificationPreference[] | undefined }
  >({
    mutationFn: (items) => updateNotificationPreferences(token as string, projectId, items),
    onMutate: async (items) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.push.preferences(projectId) });
      const previous = queryClient.getQueryData<NotificationPreference[]>(queryKeys.push.preferences(projectId));
      queryClient.setQueryData(queryKeys.push.preferences(projectId), items);
      return { previous };
    },
    onError: (_error, _items, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.push.preferences(projectId), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.push.preferences(projectId) });
    },
  });
};

export const useSendTestNotificationMutation = (token: string | null, projectId: string) => {
  return useMutation<{ delivered: number }, Error, string | null | undefined>({
    mutationFn: (endpoint) => sendTestPushNotification(token as string, projectId, endpoint),
  });
};
